import { apiFetch } from './client';
import { lookupOrders } from './orders';

export const SUPPORT_ERRORS = {
  subject_and_message_required: 'Please fill in both the subject and description.',
  unauthorized: 'Your session has expired — please log in again.',
  forbidden: 'You do not have permission to do this.',
  customer_sub_and_id_required: 'This ticket is missing customer information — refresh and try again.',
  invalid_status: 'Choose a valid ticket status.',
  not_found: 'This ticket could not be found — it may have already been updated.',
  already_resolved: 'This ticket already has a dispose / return decision.',
  credit_amount_required_for_dispose: 'Enter the wallet credit amount for dispose approval.',
  credit_amount_must_be_positive: 'Credit amount must be greater than zero.',
  invalid_credit_amount: 'Enter a valid credit amount.',
  order_id_required_for_require_return: 'This ticket needs a linked order before you can require a return.',
  invalid_resolution: 'Choose dispose + credit, or require return.',
  resolution_not_applied:
    'Server did not apply dispose/credit. Deploy the latest bhima-api support PATCH (resolution + wallet credit), then retry.',
  credit_not_applied:
    'Ticket saved but wallet credit was not confirmed. Deploy/check bhima-api support dispose flow, then retry on an open ticket.',
};

/** Same values as return order_channel — SUBS vs grocery DELIVERY. */
export const TICKET_ORDER_CHANNEL = {
  DELIVERY: 'DELIVERY',
  SUBS: 'SUBS',
};

export const TICKET_ORDER_CHANNEL_LABELS = {
  [TICKET_ORDER_CHANNEL.DELIVERY]: 'Grocery',
  [TICKET_ORDER_CHANNEL.SUBS]: 'Subscription',
};

/** Ops decision on order-linked tickets (failed delivery / report issue). */
export const TICKET_RESOLUTION = {
  DISPOSE: 'DISPOSE',
  REQUIRE_RETURN: 'REQUIRE_RETURN',
};

export const TICKET_RESOLUTION_LABELS = {
  [TICKET_RESOLUTION.DISPOSE]: 'Dispose + wallet credit',
  [TICKET_RESOLUTION.REQUIRE_RETURN]: 'Require return / pickup',
};

/**
 * Parse the boilerplate message the customer app builds for subscription issues:
 * "{label} ({CODE}) for {product} on subscription order {id}. Line value: {amt}. Reviewer decides: … {optional note}"
 */
export function parseSubscriptionIssueMessage(message) {
  const text = String(message || '').trim();
  if (!text) return null;
  const match = text.match(
    /^(.+?)\s*\(([A-Z][A-Z0-9_]*)\)\s+for\s+(.+?)\s+on\s+subscription\s+order\s+([A-Za-z0-9_-]+)\.\s*Line value:\s*([^.]*)\.\s*Reviewer decides:[^.]*\.(?:\s*(.*))?$/s,
  );
  if (!match) return null;
  const customerNote = (match[6] || '').trim();
  return {
    reasonLabel: match[1].trim(),
    reasonCode: match[2].trim(),
    productName: match[3].trim(),
    orderId: match[4].trim(),
    lineValue: match[5].trim(),
    customerNote: customerNote || null,
  };
}

/** Extract a numeric rupee amount from free text like "₹35" or "Line value: 120". */
export function parseSuggestedCreditAmount(raw) {
  if (raw == null || raw === '') return null;
  const match = String(raw).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const amount = Number(match[1]);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function formatTicketListSummary(ticket) {
  const parsed = parseSubscriptionIssueMessage(ticket?.message);
  if (parsed) return parsed.reasonLabel;
  return ticket?.message || '';
}

export function formatTicketListTitle(ticket) {
  const parsed = parseSubscriptionIssueMessage(ticket?.message);
  if (parsed?.productName) return parsed.productName;
  if (ticket?.subject && ticket.subject !== 'Subscription delivery issue') {
    return ticket.subject;
  }
  return ticket?.subject || 'Support ticket';
}

function normalizeTicketChannel(raw) {
  const value = String(
    raw?.orderChannel
    || raw?.order_channel
    || raw?.channel
    || '',
  ).trim().toUpperCase();
  return value === TICKET_ORDER_CHANNEL.SUBS
    ? TICKET_ORDER_CHANNEL.SUBS
    : value === TICKET_ORDER_CHANNEL.DELIVERY
      ? TICKET_ORDER_CHANNEL.DELIVERY
      : null;
}

function normalizeTicket(raw) {
  if (!raw) return null;
  const resolution = raw.resolution
    ? String(raw.resolution).toUpperCase()
    : null;
  const creditRaw = raw.credit_amount ?? raw.creditAmount;
  let creditAmount = null;
  if (creditRaw != null && creditRaw !== '') {
    const n = Number(creditRaw);
    if (Number.isFinite(n)) creditAmount = n;
  }
  return {
    ticketId: raw.ticket_id,
    customerSub: raw.customer_sub,
    subject: raw.subject,
    message: raw.message,
    supportType: raw.support_type || null,
    supportTypeLabel: raw.support_type_label || null,
    orderId: raw.order_id || null,
    orderChannel: normalizeTicketChannel(raw),
    status: raw.status,
    opsNote: raw.ops_note || null,
    resolution: resolution && TICKET_RESOLUTION_LABELS[resolution] ? resolution : null,
    resolutionLabel: raw.resolution_label
      || (resolution ? TICKET_RESOLUTION_LABELS[resolution] : null)
      || null,
    creditAmount,
    returnIds: Array.isArray(raw.return_ids)
      ? raw.return_ids.filter(Boolean)
      : Array.isArray(raw.returnIds)
        ? raw.returnIds.filter(Boolean)
        : [],
    resolvedAt: raw.resolved_at || raw.resolvedAt || null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    updatedBy: raw.updated_by || null,
    imageUrls: Array.isArray(raw.image_urls)
      ? raw.image_urls.filter(Boolean)
      : [],
  };
}

/** Resolve order.channel for tickets that only have order_id. */
async function enrichTicketChannels(tickets, token) {
  const needLookup = [
    ...new Set(
      tickets
        .filter((t) => t.orderId && !t.orderChannel)
        .map((t) => t.orderId),
    ),
  ];
  if (needLookup.length === 0) {
    return tickets.map((t) => ({
      ...t,
      orderChannel: t.orderChannel || TICKET_ORDER_CHANNEL.DELIVERY,
    }));
  }

  const channelByOrder = {};
  await Promise.all(
    needLookup.map(async (orderId) => {
      try {
        const result = await lookupOrders({ orderId }, token);
        const channel = String(result?.orders?.[0]?.channel || '').toUpperCase();
        channelByOrder[orderId] = channel === TICKET_ORDER_CHANNEL.SUBS
          ? TICKET_ORDER_CHANNEL.SUBS
          : TICKET_ORDER_CHANNEL.DELIVERY;
      } catch {
        channelByOrder[orderId] = TICKET_ORDER_CHANNEL.DELIVERY;
      }
    }),
  );

  return tickets.map((t) => ({
    ...t,
    orderChannel: t.orderChannel
      || (t.orderId ? channelByOrder[t.orderId] : null)
      || TICKET_ORDER_CHANNEL.DELIVERY,
  }));
}

export async function createSupportTicket({ subject, message, orderId, supportType, imageS3Keys }, token) {
  const raw = await apiFetch('/support/tickets', {
    method: 'POST',
    token,
    body: {
      subject,
      message,
      ...(supportType ? { support_type: supportType } : {}),
      ...(orderId ? { order_id: orderId } : {}),
      ...(imageS3Keys?.length ? { image_s3_keys: imageS3Keys } : {}),
    },
  });
  return normalizeTicket(raw?.ticket);
}

export async function listSupportTickets(status = 'OPEN', token, { channel = null, enrichChannel = true } = {}) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const raw = await apiFetch(`/support/tickets${qs}`, { token });
  let tickets = (raw?.tickets || []).map(normalizeTicket).filter(Boolean);
  if (enrichChannel || (channel && channel !== 'ALL')) {
    tickets = await enrichTicketChannels(tickets, token);
  } else {
    tickets = tickets.map((t) => ({
      ...t,
      orderChannel: t.orderChannel || TICKET_ORDER_CHANNEL.DELIVERY,
    }));
  }

  const isAllChannel = !channel || channel === 'ALL';
  if (!isAllChannel) {
    tickets = tickets.filter((t) => t.orderChannel === channel);
  }
  return tickets;
}

/**
 * Update ticket status/note, and optionally resolve with wallet credit or return pickup.
 * @param {{ customerSub: string, status?: string, opsNote?: string, resolution?: string|null, creditAmount?: number|string|null }} patch
 */
export async function updateSupportTicket(ticketId, {
  customerSub,
  status,
  opsNote,
  resolution,
  creditAmount,
} = {}, token) {
  const body = {
    customer_sub: customerSub,
    ...(status ? { status } : {}),
    ...(opsNote != null ? { ops_note: opsNote } : {}),
  };
  let requestedCredit = null;
  if (resolution) {
    body.resolution = resolution;
    if (resolution === TICKET_RESOLUTION.DISPOSE) {
      const amount = Number(creditAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        const err = new Error('credit_amount_must_be_positive');
        err.code = 'credit_amount_must_be_positive';
        throw err;
      }
      // Keep paisa-safe JSON number for API Gateway / Decimal parsing.
      requestedCredit = Math.round(amount * 100) / 100;
      body.credit_amount = requestedCredit;
    }
  }
  const raw = await apiFetch(`/support/tickets/${encodeURIComponent(ticketId)}`, {
    method: 'PATCH',
    token,
    body,
  });
  const ticket = normalizeTicket(raw?.ticket);

  // Older bhima-api ignores `resolution` and still returns 200 — do not claim credit.
  if (resolution && ticket?.resolution !== resolution) {
    const err = new Error('resolution_not_applied');
    err.code = 'resolution_not_applied';
    throw err;
  }
  if (resolution === TICKET_RESOLUTION.DISPOSE) {
    const credited = Number(ticket?.creditAmount);
    if (!Number.isFinite(credited) || credited <= 0) {
      const err = new Error('credit_not_applied');
      err.code = 'credit_not_applied';
      throw err;
    }
  }

  return ticket;
}

/** Best-effort suggested credit from ticket message line value or order total. */
export async function suggestTicketCreditAmount(ticket, token) {
  const parsed = parseSubscriptionIssueMessage(ticket?.message);
  const fromLine = parseSuggestedCreditAmount(parsed?.lineValue);
  if (fromLine != null) return fromLine;
  if (!ticket?.orderId || !token) return null;
  try {
    const result = await lookupOrders({ orderId: ticket.orderId }, token);
    const order = result?.orders?.[0];
    const total = Number(order?.amountPaid ?? order?.total ?? 0);
    return Number.isFinite(total) && total > 0 ? total : null;
  } catch {
    return null;
  }
}
